import {
  selectIElementsSlice,
  type OnlyIElementsStore,
} from "@/store/ielements-slice-selector";
import { curryAppSelector } from "@/store/reselect";
import { RootState } from "@/store/store";
import { useAppSelector } from "@/store/store-hooks";
import { areVector3ArraysEqual } from "@/utils/equality";
import {
  IElementsState,
  selectAltitudeDifferenceToFloor,
  selectAncestor,
  selectChildDepthFirst,
  selectIElementWorldPosition,
  TransformOverrides,
} from "@faro-lotv/app-component-toolbox";
import {
  IElement,
  IElementImg360,
  IElementTypeHint,
  isIElementMarkupImagePoint,
  isIElementRoomSection,
  isIElementTimeSeriesRoom,
} from "@faro-lotv/ielement-types";
import { createSelector } from "@reduxjs/toolkit";
import { Vector3 } from "three";

/** Default distance to keep between the floor and the placeholders so they don't z-fight */
export const DEFAULT_PLACEHOLDER_FLOOR_OFFSET = 0.012;

/**
 * Calculates the placeholder positions for a given set of IElements on a sheet
 *
 * @param placeholders we want to render
 * @param floorForElevation sheet whose elevation will be used to place the placeholders
 * @param heightOffset added to the positions to avoid z-fighting with other ui elements
 * @param transformOverrides Optional transform overrides to apply to the rendered elements.
 * @returns the computed list of positions for the placeholders
 */
export function useMapPlaceholderPositions(
  placeholders: IElement[],
  floorForElevation?: IElement,
  heightOffset: number = DEFAULT_PLACEHOLDER_FLOOR_OFFSET,
  transformOverrides?: TransformOverrides,
): Vector3[] {
  return useAppSelector(
    selectMapPlaceholdersPositions(
      placeholders,
      floorForElevation,
      heightOffset,
      transformOverrides,
    ),
    areVector3ArraysEqual,
  );
}

/**
 *
 * @param state The root state of the application
 * @param placeholders The IElements to compute positions for
 * @param floorForElevation The floor IElement to use for elevation adjustments
 * @param heightOffset The height offset to apply to the positions
 * @param transformOverrides Optional transform overrides to apply to the rendered elements.
 * @returns A selector that computes the positions of placeholders on a map
 */
const selectMapPlaceholdersPositions = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: RootState, placeholders: IElement[]) => placeholders,
      (
        state: RootState,
        placeholders: IElement[],
        floorForElevation: IElement | undefined,
      ) => floorForElevation,
      (
        state: RootState,
        placeholders: IElement[],
        floorForElevation: IElement | undefined,
        heightOffset: number,
      ) => heightOffset,
      (
        state: RootState,
        placeholders: IElement[],
        floorForElevation: IElement | undefined,
        heightOffset: number,
        transformOverrides?: TransformOverrides,
      ) => transformOverrides,
    ],
    computeMapPlaceholdersPositions,
  ),
);

/**
 * Special purpose store selector to compute the adjusted position for all the placeholders relative to a floor
 *
 * Need to be used with a specific comparator as the selector will generate new objects every call
 *
 * @param iElements the iElements state slice
 * @param placeholders we want to render
 * @param floorForElevation where to place the placeholders
 * @param heightOffset offset added to the positions to avoid z-fighting with other ui elements
 * @param transformOverrides Optional transform overrides to apply to the rendered elements.
 * @returns the computed list of positions for the placeholders
 */
function computeMapPlaceholdersPositions(
  iElements: IElementsState,
  placeholders: IElement[],
  floorForElevation: IElement | undefined,
  heightOffset: number,
  transformOverrides?: TransformOverrides,
): Vector3[] {
  const positions: Vector3[] = [];
  const store = { iElements };
  for (const placeholder of placeholders) {
    if (
      isIElementRoomSection(placeholder) &&
      !placeholder.parentId &&
      !!placeholder.pose &&
      placeholder.pose.pos
    ) {
      // For temporary orphan new location marker, its position in pose is the world position
      const worldPos = placeholder.pose.pos;
      const heightFix =
        (floorForElevation
          ? worldPos.y -
            selectIElementWorldPosition(
              floorForElevation.id,
              transformOverrides,
            )(store)[1]
          : 0) - heightOffset;
      positions.push(
        new Vector3(worldPos.x, worldPos.y - heightFix, worldPos.z),
      );
    } else {
      // If the current 360 belongs to a time series, we want
      // to use the room position and not the 360 one
      const timeSeries = selectAncestor(
        placeholder,
        isIElementTimeSeriesRoom,
      )(store);

      const id = timeSeries ? timeSeries.id : placeholder.id;

      const heightFix =
        (floorForElevation
          ? selectAltitudeDifferenceToFloor(
              id,
              floorForElevation.id,
              transformOverrides,
            )(store)
          : 0) - heightOffset;
      const worldPos = selectIElementWorldPosition(
        id,
        transformOverrides,
      )(store);
      positions.push(
        new Vector3(worldPos[0], worldPos[1] - heightFix, worldPos[2]),
      );
    }
  }
  return positions;
}

/**
 *
 * @param panos All panos belonging to an odometry path.
 * @returns The list of indices of POIs within the argument panos.
 */
export const selectPathPoiIndices = curryAppSelector(
  createSelector(
    selectIElementsSlice,
    (state: OnlyIElementsStore, panos: IElementImg360[]) => panos,
    (iElements, panos): number[] => {
      const indices: number[] = [];
      for (let i = 0; i < panos.length; i++) {
        const pano = panos[i];
        // 'poiHighRes' is only for flash scans. 'poiLowRes' or 'poi'
        // are used when the user stops on a POI to capture a pano.
        if (
          pano.typeHint === IElementTypeHint.poiLowRes ||
          pano.typeHint === IElementTypeHint.poi ||
          selectChildDepthFirst(pano, isIElementMarkupImagePoint)({ iElements })
        ) {
          indices.push(i);
        }
      }
      return indices;
    },
  ),
);
